import React from 'react'
import {ajax, postJson, resolveRoute} from '../helpers/ajax'
import {getState, linkState, setState, updateState} from "../helpers/react-helpers"
import * as kb from '../kb'
import * as urls from '../external-urls'
import PubSub from 'pubsub-js'
import * as topics from '../pubsub-topics'
import RDS from 'react-dom/server'
import ErrorMessage from './ErrorMessage'
import ButtonRow from './ButtonRow'
import PaymentAllocation from './PaymentAllocation'
import {GridCol, GridRow, GridSection, HtmlGridForm} from "./grid-form"
import AioDollar from "./aio-inputs/AioDollar"
import AioSelect from "./aio-inputs/AioSelect"
import {notifyError} from '../notify'
import {ExternalLink} from './external-links'
import {notifyErrorResponse} from '../notify-response'
import type {StripePaymentInfoResponse} from './stripe-payment-info'
import {loadScript} from '../util/load-script'
import {mergeIn} from '../util/collections'
import {parseFloat} from '../util/math'

type PaymentAllocationRecord = Record<string,number>

interface FormData {
    amount: string
    source_id: string
    currency: string
    save_cc: boolean
    alloc: PaymentAllocationRecord
}

interface StripeChargeFormState {
    loading: boolean
    saving: boolean
    formData: FormData
    options: {
        stripe_currencies: string[]
        credit_cards: any[]
        stripe_customer_id: string
        customer_name: string
        customer_number: string
        bookings: any[]
    }
}

interface StripeChargeFormProps {
    bookingId: number
    testMode?: boolean;
    stripePublishableKey?: string;
    stripeUserId?: string;
    capture: boolean;
}

export default class StripeChargeForm extends React.PureComponent<StripeChargeFormProps, StripeChargeFormState> {

    constructor(props: StripeChargeFormProps) {
        super(props);
        this.state = {
            loading: true,
            saving: false,
            formData: {
                amount: '888.88',
                source_id: '',
                currency: '',
                save_cc: true,
                alloc: {},
            },
            options: {
                stripe_currencies: [],
                credit_cards: [],
                stripe_customer_id: '',
                customer_name: 'Unknown',
                customer_number: '0',
                bookings: [],
            },
            // card_id: '',
            // options: {},
        }
    }

    refresh = () => {
        this.setState({loading: true});

        return ajax<StripePaymentInfoResponse>(resolveRoute('api.stripePaymentInfo', {
            bookingId: this.props.bookingId,
        }))
            .then(res => {
                if (res.ok) {
                    updateState.call(this, {
                        options: {$apply: mergeIn(res.data)},
                        formData: {
                            amount: {$set: res.data.amount_owing},
                            source_id: {$set: res.data.default_cc},
                            currency: {$set: res.data.default_currency || res.data.stripe_currencies[0][0]},
                            alloc: {$set: {}},
                        },
                    });
                } else {
                    notifyErrorResponse({message: "Error loading payment info", ...res.data})
                }
            })
            .finally(() => {
                this.setState({loading: false});
            });

    };

    componentDidMount() {
        this.refresh();

        loadScript(urls.stripeV2Api, 'Stripe').then((Stripe: any) => {
            Stripe.setPublishableKey(this.props.stripePublishableKey);
        }, () => {
            kb.notify({
                text: "Failed to load Stripe API",
                className: "error",
            });
        });
    }

    render() {
        const processing = this.state.loading || this.state.saving;
        const invalid = !this.state.options.credit_cards.length;

        return (
            <HtmlGridForm onSubmit={this.submit}>
                <GridSection>
                    <GridRow>
                        <GridCol minWidth={100}>
                            <AioDollar label="Amount" id="stripe-amount" disabled={processing} loading={processing} {...linkState.call(this, 'formData.amount')} currency={this.state.formData.currency} />
                        </GridCol>
                        <GridCol minWidth={200}>
                            <AioSelect label="Currency" options={this.state.options.stripe_currencies} id="stripe-currency" {...linkState.call(this, 'formData.currency')} disabled={processing} loading={processing}/>
                        </GridCol>
                    </GridRow>
                    <GridRow>
                        <GridCol>
                            {this.state.options.credit_cards.length ? <>
                            <AioSelect placeholder="— Choose a Card —" label={<>Stored
                                Card{this.state.options.credit_cards.length == 1 ? '' : 's'} for {this.state.options.customer_name} (#{this.state.options.customer_number})</>} options={this.state.options.credit_cards} id="saved-cards" {...linkState.call(this, 'formData.source_id')}>
                            </AioSelect>
                            {this.state.options.stripe_customer_id ? <div><ExternalLink
                                href={`https://dashboard.stripe.com/${this.props.testMode ? 'test/' : ''}customers/${encodeURIComponent(this.state.options.stripe_customer_id)}`}
                                >Edit on Stripe</ExternalLink></div> : null}
                            </> : (!this.state.loading ? <p>Please press "Store Card" to create a payment method first.</p> : null)}
                        </GridCol>
                    </GridRow>

                    {this.state.options.bookings && this.state.options.bookings.length > 1 ? <div className="row">
                        <div className="col">
                            <PaymentAllocation
                                setAlloc={(id, val) => val === undefined ? setState.call(this, 'formData.alloc', id) : setState.call(this, `formData.alloc.${id}`, val)}
                                getAlloc={id => getState.call(this, `formData.alloc.${id}`)}
                                chargeAmount={getState.call(this, `formData.amount`)}
                                bookings={this.state.options.bookings} alloc={this.state.formData.alloc}/>
                        </div>
                    </div> : null}
                </GridSection>
                <ButtonRow>
                    <ButtonRow.Left className="left btn-group">
                        <button id="stripe-charge-btn" type="submit" className="stripe-connect" style={{float: 'left'}}
                                name="action" disabled={processing||invalid}>
                            <span>{this.props.capture ? "Charge" : "Pre-auth"} with Stripe</span></button>
                    </ButtonRow.Left>
                </ButtonRow>
            </HtmlGridForm>
        )
    }

    submit = (ev: any) => {
        ev.preventDefault();

        if (this.state.formData.source_id) {
            this.setState({saving: true});
            postJson<any,{id:string|number}>(resolveRoute('payments.makeStripePayment', {id: this.props.bookingId}), {
                ...this.state.formData,
                capture: this.props.capture,
            })
                .then(this.handlePayment)
                .finally(() => {
                    this.setState({saving: false});
                });
        } else {
            notifyError(`Please pick a card. If none are available, use the "Store Card" tab.`)
        }
    };

    handlePayment = (res: any) => {
        if (res.status >= 200 && res.status < 300) {
            kb.notify({
                text: this.props.capture ? "Card charged" : "Card pre-authorized",
                className: "success",
            });
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            const newAmountOwing = parseFloat(this.state.options.amount_owing, 0) - parseFloat(this.state.amount, 0);
            this.setState({amount: newAmountOwing.toFixed(2)});
            this.refresh();
            PubSub.publish(topics.PaymentEntered);
        } else {
            // console.log('there was an error',res,res.data)
            if (res.data.errors) {
                kb.notify({
                    html: RDS.renderToStaticMarkup(ErrorMessage({errors: res.data.errors})),
                    className: "warning",
                });
            } else {
                kb.notify({
                    text: "An unknown error occurred",
                    className: "error",
                });
            }
        }
    };
}

